import * as lz4 from "lz4js"; // Use lz4js for the browser

/**
 * Compresses JSON data and returns a Blob containing the compressed data.
 * @param {object} jsonData The JavaScript object to compress.
 * @returns {Promise<Blob>} The compressed data as a Blob.
 */
export const compress = async jsonData => {
  try {
    const jsonString = JSON.stringify(jsonData);
    const textEncoder = new TextEncoder();
    const originalData = textEncoder.encode(jsonString);

    const compressedData = lz4.compress(originalData);

    return new Blob([compressedData], { type: "application/octet-stream" });
  } catch (error) {
    console.error("Error compressing JSON:", error);
    throw error;
  }
};

/**
 * Reads a user-selected compressed file (.lz4), decompresses it, and parses it as JSON.
 * @param {File} fileObject The File object from an <input type="file"> element.
 * @returns {Promise<object|null>} A promise that resolves to the parsed JSON object, or null on error/empty file.
 */
export const decompress = async fileObject => {
  if (!fileObject) {
    console.warn("No file provided to decompressJsonFromLz4File.");
    return null;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = event => {
      try {
        const compressedData = new Uint8Array(event.target.result);
        if (compressedData.length === 0) {
          console.warn("File is empty.");
          resolve(null);
          return;
        }

        const decompressedData = lz4.decompress(compressedData);

        const textDecoder = new TextDecoder();
        const jsonString = textDecoder.decode(decompressedData);

        const jsonData = JSON.parse(jsonString);

        resolve(jsonData);
      } catch (error) {
        console.error("Error reading or decompressing file:", error);
        reject(error);
      }
    };

    reader.onerror = error => {
      console.error("FileReader error:", error);
      reject(error);
    };

    reader.readAsArrayBuffer(fileObject);
  });
};

/**
 * Decompresses an ArrayBuffer or Uint8Array.
 * @param {ArrayBuffer|Uint8Array} buffer The buffer to decompress.
 * @returns {object} The parsed JSON object.
 */
export const decompressBuffer = buffer => {
  try {
    const compressedData = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const decompressedData = lz4.decompress(compressedData);
    const textDecoder = new TextDecoder();
    const jsonString = textDecoder.decode(decompressedData);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Error decompressing buffer:", error);
    throw error;
  }
};
